import React from 'react'

// Common Components
import CustomBadge from '../Common/Badge/CustomBadge'
import { badgeColors } from '../Common/Color/Color'
import { fontSizes } from '../Common/Typography/TypographyStyles'

// Icons
import StarIcon from '@mui/icons-material/Star'

interface PointsValue {
  category: string
  pointsNum: number
}

interface EventBadgesProps {
  value: string | PointsValue // value can be either string or PointsValue object
  badgeType: 'statusBadge' | 'completeBadge' | 'categoryBadge' | 'pointsBadge'
}

const EventBadges: React.FC<EventBadgesProps> = ({ value, badgeType }) => {
  const statusMapping: Record<string, string> = {
    active: badgeColors.greenSanFelix,
    inactive: badgeColors.redDark,
  }

  const categoryColorMapping: Record<string, { label: string; color: string }> = {
    TIDS: {
      label: 'TIDS',
      color: badgeColors.brightBlue,
    },
    happyhere: {
      label: '#HAPPYHERE',
      color: badgeColors.purpleTelus,
    },
    teamEvent: {
      label: 'TEAM EVENT',
      color: badgeColors.softPink,
    },
    COP: {
      label: 'COP',
      color: badgeColors.teal,
    },
  }

  const category = categoryColorMapping[String(value)]

  if (badgeType === 'statusBadge') {
    if (typeof value === 'string') {
      return (
        <CustomBadge
          label={value
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')}
          size={fontSizes.size12}
          color={statusMapping[value]}
          width={80}
          rounded
        />
      )
    }
    return null // Handle case where value is not a string
  }

  if (badgeType === 'completeBadge') {
    return (
      <CustomBadge
        label={'Completed'}
        size={fontSizes.size12}
        color={badgeColors.deepPurple}
        width={80}
        rounded
      />
    )
  }

  if (badgeType === 'categoryBadge') {
    return (
      <CustomBadge
        label={category?.label || String(value)} // Ensuring value is treated as string
        size={fontSizes.size12}
        color={category?.color || badgeColors.brightBlue} // Provide a default color if category is undefined
        width={100}
      />
    )
  }

  if (badgeType === 'pointsBadge') {
    if (typeof value === 'object' && value !== null) {
      return (
        <CustomBadge
          label={
            value.category === 'COP'
              ? value.pointsNum > 0
                ? `${value.pointsNum} COP Points`
                : '0 COP Point'
              : value.pointsNum > 0
                ? `${value.pointsNum} Star Points`
                : '0 Star Point'
          }
          startIcon={<StarIcon fontSize='inherit' />}
          color={badgeColors.complementaryYellow}
          size={fontSizes.size12}
        />
      )
    } else {
      return (
        <CustomBadge
          label='Invalid Value'
          size={fontSizes.size12}
          color={badgeColors.redDark}
          width={100}
        />
      )
    }
  }

  return null // Handle any other cases as necessary
}

export default EventBadges
